import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { selectIsSubCompanyUser } from 'store/auth/selectors';
import { fetchMessagesAction } from 'store/teamChat/actions';
import {
  selectTeamChatIsLoadingMessages,
  selectTeamChatMessagesLoadedOnce,
} from 'store/teamChat/selectors';

import { getInternalChatId } from '../TeamChat.utils';

// Powers the entity-reference badges/rail (DEV-5448) OUTSIDE the loan chat drawer (Financial Info
// tables, borrower cards) — those surfaces need the internal channel's messages in the teamChat
// store even when the drawer has never been opened (the drawer only fetches messages once its
// internal channel panel becomes active — see LoanEditorChatChannelPanel). Call ONCE per page
// (e.g. from FinancialInfoBody / Borrowers), not per row — badges just read the resulting store
// state via useEntityReferenceIndex.
const useEnsureChannelMessagesLoaded = applicationId => {
  const dispatch = useDispatch();
  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
  // Sub-company (broker) users are barred from the internal channel — the same gate the loan chat
  // drawer applies to `isInternalChannelAvailable`. A null chatId here cascades cleanly: no fetch
  // below, and every downstream badge / "Discuss in chat" consumer (which only render/enable off a
  // truthy chatId) degrades to their existing no-chatId no-op — no separate gate needed there.
  const chatId = isSubCompanyUser ? null : getInternalChatId(applicationId);

  const isLoading = useSelector(selectTeamChatIsLoadingMessages(chatId));
  const isLoadedOnce = useSelector(selectTeamChatMessagesLoadedOnce(chatId));
  // Armed with the chatId the fetch was fired for (not just a bool) so a chatId change — the loan
  // navigated away and back, or a different application's row mounts this hook — re-arms rather
  // than staying permanently "in flight" for a chat that will never resolve.
  const inFlightChatIdRef = useRef(null);

  useEffectSafeStrictMode(() => {
    if (!chatId || isLoading || isLoadedOnce || inFlightChatIdRef.current === chatId) {
      return;
    }

    inFlightChatIdRef.current = chatId;
    dispatch(fetchMessagesAction({ chatId }));
  }, [chatId, isLoading, isLoadedOnce, dispatch]);

  return chatId;
};

export default useEnsureChannelMessagesLoaded;
